import React from 'react';
import { Link } from 'react-router-dom';

const PricingPage = () => {
  const pricingPlans = [
    {
      level: 'Grade 7',
      price: 'K500',
      duration: 'per year',
      description: 'Perfect for primary school students preparing for their final examinations',
      features: [
        'Core Grade 7 subjects included',
        'Downloadable Past Papers',
        'AI-powered tutor support',
        'Interactive exam simulation',
        'Web platform access'
      ],
      color: 'blue'
    },
    {
      level: 'Grade 12',
      price: 'K1000',
      duration: 'per year',
      description: 'Advanced preparation for senior secondary and university entrance exams',
      features: [
        'Core Grade 12 subjects included',
        'Downloadable Past Papers',
        'AI-powered tutor support',
        'Interactive exam simulation',
        'Web platform access'
      ],
      color: 'purple'
    }
  ];

  const faqs = [
    {
      question: 'What payment methods do you accept?',
      answer: 'We accept mobile money payments from all major Zambian providers including Airtel Money, MTN Mobile Money, and Zamtel Money.'
    },
    {
      question: 'Can I change my subscription plan?',
      answer: 'Yes, you can upgrade your subscription plan at any time. Contact our support team for assistance with plan changes.'
    },
    {
      question: 'Is there a free trial available?',
      answer: 'Zed Scholar is a subscription service with no free trial. We believe in providing immediate access to all features from day one.'
    },
    {
      question: 'What subjects are covered?',
      answer: 'We cover core subjects including Mathematics, English, Science, Social Studies, and selected elective subjects based on each grade level.'
    },
    {
      question: 'Can I access content offline?',
      answer: 'Content is accessible through our web platform for convenient learning.'
    },
    {
      question: 'What makes Zed Scholar different?',
      answer: 'Our service includes AI tutor support, expert-curated content, detailed solutions, basic progress tracking, and comprehensive study materials.'
    }
  ];

  return (
    <div className="pricing-page">
      <div className="container">
        <div className="page-header">
          <h1>Plans for Academic Excellence</h1>
          <p>Invest in your future with Zed Scholar's comprehensive features</p>
        </div>

        {/* Pricing Cards */}
        <section className="pricing-section">
          <div className="pricing-cards">
            {pricingPlans.map((plan, index) => (
              <div key={index} className={`pricing-card ${plan.color}`}>
                <div className="plan-header">
                  <h3>{plan.level}</h3>
                  <div className="price">
                    <span className="amount">{plan.price}</span>
                    <span className="duration">{plan.duration}</span>
                  </div>
                  <p className="plan-description">{plan.description}</p>
                </div>

                <div className="plan-features">
                  <ul className="features-list">
                    {plan.features.map((feature, idx) => (
                      <li key={idx}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="20,6 9,17 4,12"/>
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="plan-action">
                  <Link to="/register" className="btn btn-primary btn-full">
                    Get Started
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Comparison Table */}
        <section className="comparison-section">
          <h2>Compare Features</h2>
          <div className="comparison-table">
            <table>
              <thead>
                <tr>
                  <th>Features</th>
                  <th>Grade 7</th>
                  <th>Grade 12</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Core Subjects</td>
                  <td>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20,6 9,17 4,12"/>
                    </svg>
                  </td>
                  <td>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20,6 9,17 4,12"/>
                    </svg>
                  </td>
                </tr>
                <tr>
                  <td>Past Papers</td>
                  <td>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20,6 9,17 4,12"/>
                    </svg>
                  </td>
                  <td>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20,6 9,17 4,12"/>
                    </svg>
                  </td>
                </tr>
                <tr>
                  <td>Practice Tests</td>
                  <td>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20,6 9,17 4,12"/>
                    </svg>
                  </td>
                  <td>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20,6 9,17 4,12"/>
                    </svg>
                  </td>
                </tr>
                <tr>
                  <td>Web Platform Access</td>
                  <td>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20,6 9,17 4,12"/>
                    </svg>
                  </td>
                  <td>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20,6 9,17 4,12"/>
                    </svg>
                  </td>
                </tr>
                <tr>
                  <td>AI Tutor Support</td>
                  <td>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20,6 9,17 4,12"/>
                    </svg>
                  </td>
                  <td>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20,6 9,17 4,12"/>
                    </svg>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="faq-section">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-list">
            {faqs.map((faq, index) => (
              <div key={index} className="faq-item">
                <h3>{faq.question}</h3>
                <p>{faq.answer}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="cta-section">
          <h2>Start Your Learning Journey</h2>
          <p>Join thousands of successful students who chose Zed Scholar for academic excellence</p>
          <Link to="/register" className="btn btn-primary btn-large">
            Get Started Now
          </Link>
        </section>
      </div>
    </div>
  );
};

export default PricingPage;
